import { useState, useRef, useCallback, useEffect } from 'react';
import { getAgentWS } from '../utils/ws';

const SAMPLE_RATE = 16000;
const TTS_SAMPLE_RATE = 24000;
const BUFFER_SIZE = 4096;

const DEBUG_ONLY_FIRST_CHUNK = false;
const DEBUG_AUDIO = true;

const PRE_BUFFER_CHUNKS = 10;
const PRE_BUFFER_TIMEOUT_MS = 3000;
const TARGET_RMS = 0.1;
const MAX_GAIN = 10.0;
const MIN_GAIN = 0.1;

function resampleAudio(inputData, inputSampleRate, outputSampleRate) {
    if (inputSampleRate === outputSampleRate) {
        return inputData;
    }
    const ratio = inputSampleRate / outputSampleRate;
    const outputLength = Math.floor(inputData.length / ratio);
    const output = new Float32Array(outputLength);
    for (let i = 0; i < outputLength; i++) {
        const srcIndex = i * ratio;
        const srcIndexFloor = Math.floor(srcIndex);
        const srcIndexCeil = Math.min(srcIndexFloor + 1, inputData.length - 1);
        const fraction = srcIndex - srcIndexFloor;
        output[i] = inputData[srcIndexFloor] * (1 - fraction) + inputData[srcIndexCeil] * fraction;
    }
    return output;
}

function float32ToPCM16(float32Array) {
    const buffer = new ArrayBuffer(float32Array.length * 2);
    const view = new DataView(buffer);
    for (let i = 0; i < float32Array.length; i++) {
        const s = Math.max(-1, Math.min(1, float32Array[i]));
        view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return buffer;
}

function calculateRMS(audioData) {
    let sum = 0;
    for (let i = 0; i < audioData.length; i++) {
        sum += audioData[i] * audioData[i];
    }
    return Math.sqrt(sum / audioData.length);
}

function normalizeAudio(audioData, targetRMS = TARGET_RMS) {
    const originalRMS = calculateRMS(audioData);
    if (originalRMS < 0.001) {
        return { data: audioData, gain: 1.0, originalRMS };
    }
    let gain = targetRMS / originalRMS;
    gain = Math.max(MIN_GAIN, Math.min(MAX_GAIN, gain));
    const normalized = new Float32Array(audioData.length);
    for (let i = 0; i < audioData.length; i++) {
        normalized[i] = Math.max(-1, Math.min(1, audioData[i] * gain));
    }
    return { data: normalized, gain, originalRMS };
}

export function useVoiceChat({
    sessionId,
    model,
    language = 'auto',
    voiceId,
    shareId,
    onTranscript,
    onAgentResponse,
    onSSEEvent,
    onMessageStart,
    onMessageEnd,
    onError,
    onConnected,
    onDisconnected,
}) {
    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [partialTranscript, setPartialTranscript] = useState('');

    const audioContextRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const processorRef = useRef(null);
    const isRecordingRef = useRef(false);
    const isActiveRef = useRef(false);

    const ttsContextRef = useRef(null);
    const ttsNextTimeRef = useRef(0);
    const ttsSourceNodesRef = useRef([]);
    const ttsGainNodeRef = useRef(null);
    const ttsQueueRef = useRef([]);
    const ttsIsProcessingRef = useRef(false);
    const ttsChunkCountRef = useRef(0);
    const ttsScheduledCountRef = useRef(0);
    const ttsPreBufferTimerRef = useRef(null);
    const ttsIsBufferingRef = useRef(true);
    const ttsFirstChunkTimeRef = useRef(0);
    const ttsLastChunkTimeRef = useRef(0);

    const callbacksRef = useRef({
        onTranscript, onAgentResponse, onSSEEvent,
        onMessageStart, onMessageEnd, onError, onConnected, onDisconnected,
    });
    callbacksRef.current = {
        onTranscript, onAgentResponse, onSSEEvent,
        onMessageStart, onMessageEnd, onError, onConnected, onDisconnected,
    };

    const initTTSContext = useCallback(async () => {
        if (!ttsContextRef.current) {
            ttsContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
                sampleRate: TTS_SAMPLE_RATE,
            });
            ttsGainNodeRef.current = ttsContextRef.current.createGain();
            ttsGainNodeRef.current.gain.value = 1.0;
            ttsGainNodeRef.current.connect(ttsContextRef.current.destination);
            ttsNextTimeRef.current = 0;
        }
        if (ttsContextRef.current.state === 'suspended') {
            await ttsContextRef.current.resume();
        }
        return ttsContextRef.current;
    }, []);

    const processAudioQueue = useCallback(async () => {
        if (ttsIsProcessingRef.current) return;
        ttsIsProcessingRef.current = true;

        try {
            while (ttsQueueRef.current.length > 0) {
                const pcmData = ttsQueueRef.current.shift();
                if (!pcmData) continue;

                ttsScheduledCountRef.current++;
                const scheduleNum = ttsScheduledCountRef.current;
                const ctx = await initTTSContext();

                const pcm16 = new Int16Array(pcmData);
                const float32 = new Float32Array(pcm16.length);
                for (let i = 0; i < pcm16.length; i++) {
                    float32[i] = pcm16[i] / 32768.0;
                }

                const audioBuffer = ctx.createBuffer(1, float32.length, TTS_SAMPLE_RATE);
                audioBuffer.getChannelData(0).set(float32);
                const bufferDuration = audioBuffer.duration;

                const source = ctx.createBufferSource();
                source.buffer = audioBuffer;
                source.connect(ttsGainNodeRef.current);

                const currentTime = ctx.currentTime;
                let startTime;

                if (ttsNextTimeRef.current <= currentTime) {
                    startTime = currentTime + 0.05;
                } else {
                    startTime = ttsNextTimeRef.current;
                }

                source.start(startTime);
                ttsNextTimeRef.current = startTime + bufferDuration;
                ttsSourceNodesRef.current.push(source);

                if (ttsSourceNodesRef.current.length > 50) {
                    ttsSourceNodesRef.current = ttsSourceNodesRef.current.slice(-20);
                }

                if (DEBUG_AUDIO && scheduleNum % 10 === 1) {
                    console.log(`[TTS] Schedule #${scheduleNum}: ${(bufferDuration * 1000).toFixed(0)}ms`);
                }
            }
        } catch (error) {
            console.error('[VoiceChat] Audio processing error:', error);
        } finally {
            ttsIsProcessingRef.current = false;
        }
    }, [initTTSContext]);

    const queueAudioPlayback = useCallback((pcmData) => {
        ttsChunkCountRef.current++;
        const chunkNum = ttsChunkCountRef.current;
        const now = Date.now();

        if (chunkNum === 1) {
            ttsFirstChunkTimeRef.current = now;
        }
        ttsLastChunkTimeRef.current = now;

        if (DEBUG_ONLY_FIRST_CHUNK && chunkNum > 1) return;

        ttsQueueRef.current.push(pcmData);

        if (ttsIsBufferingRef.current) {
            if (chunkNum === 1) {
                ttsPreBufferTimerRef.current = setTimeout(() => {
                    if (ttsIsBufferingRef.current && ttsQueueRef.current.length > 0) {
                        ttsIsBufferingRef.current = false;
                        processAudioQueue();
                    }
                }, PRE_BUFFER_TIMEOUT_MS);
            }

            if (ttsQueueRef.current.length >= PRE_BUFFER_CHUNKS) {
                if (ttsPreBufferTimerRef.current) {
                    clearTimeout(ttsPreBufferTimerRef.current);
                    ttsPreBufferTimerRef.current = null;
                }
                ttsIsBufferingRef.current = false;
                processAudioQueue();
            }
        } else {
            processAudioQueue();
        }
    }, [processAudioQueue]);

    const stopTTSPlayback = useCallback(() => {
        ttsQueueRef.current = [];
        ttsSourceNodesRef.current.forEach(source => {
            try { source.stop(); } catch {}
        });
        ttsSourceNodesRef.current = [];
        ttsNextTimeRef.current = 0;
        ttsIsProcessingRef.current = false;
        ttsChunkCountRef.current = 0;
        ttsScheduledCountRef.current = 0;
        ttsFirstChunkTimeRef.current = 0;
        ttsLastChunkTimeRef.current = 0;
        if (ttsPreBufferTimerRef.current) {
            clearTimeout(ttsPreBufferTimerRef.current);
            ttsPreBufferTimerRef.current = null;
        }
        ttsIsBufferingRef.current = true;
    }, []);

    const cleanupTTSContext = useCallback(() => {
        stopTTSPlayback();
        ttsQueueRef.current = [];
        if (ttsContextRef.current) {
            ttsContextRef.current.close();
            ttsContextRef.current = null;
        }
        ttsGainNodeRef.current = null;
    }, [stopTTSPlayback]);

    const cleanupMicrophone = useCallback(() => {
        isRecordingRef.current = false;
        if (processorRef.current) {
            processorRef.current.disconnect();
            processorRef.current = null;
        }
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        setIsRecording(false);
    }, []);

    const handleVoiceEvent = useCallback((event) => {
        const { type, data } = event;
        const cbs = callbacksRef.current;

        switch (type) {
            case 'voice_agent_ready':
                console.log('[VoiceChat] Voice agent ready, session:', event.session_id);
                setIsConnected(true);
                cbs.onConnected?.();
                break;

            case 'stt_chunk':
                setPartialTranscript(data?.text || '');
                break;

            case 'stt_output':
                setPartialTranscript('');
                cbs.onTranscript?.(data?.text, data?.confidence);
                break;

            case 'tts_start':
                setIsSpeaking(true);
                ttsNextTimeRef.current = 0;
                ttsChunkCountRef.current = 0;
                ttsScheduledCountRef.current = 0;
                ttsFirstChunkTimeRef.current = 0;
                ttsLastChunkTimeRef.current = 0;
                if (ttsPreBufferTimerRef.current) {
                    clearTimeout(ttsPreBufferTimerRef.current);
                    ttsPreBufferTimerRef.current = null;
                }
                ttsIsBufferingRef.current = true;
                break;

            case 'tts_end': {
                const ctx = ttsContextRef.current;
                if (ctx && ttsNextTimeRef.current > ctx.currentTime) {
                    const remainingTime = (ttsNextTimeRef.current - ctx.currentTime) * 1000;
                    setTimeout(() => setIsSpeaking(false), remainingTime + 100);
                } else {
                    setIsSpeaking(false);
                }
                break;
            }

            case 'no_speech_timeout':
            case 'silence_timeout':
                cbs.onDisconnected?.(type);
                break;

            default:
                break;
        }
    }, []);

    const handleBinaryMessage = useCallback((data) => {
        if (!isActiveRef.current) return;
        queueAudioPlayback(data);
    }, [queueAudioPlayback]);

    const handleAgentEvent = useCallback((event) => {
        if (!isActiveRef.current) return;
        const cbs = callbacksRef.current;

        switch (event.type) {
            case 'message_start':
                cbs.onMessageStart?.({
                    session_id: event.session_id,
                    is_new_session: event.is_new_session,
                    is_new_session_from_share: event.is_new_session_from_share,
                    timestamp: event.timestamp,
                });
                break;

            case 'content_block_start':
            case 'content_block_delta':
            case 'content_block_stop':
                cbs.onSSEEvent?.(event);
                if (event.type === 'content_block_delta') {
                    const delta = event.delta || {};
                    if (delta.type === 'text_delta' && delta.text) {
                        cbs.onAgentResponse?.(delta.text, false);
                    }
                }
                break;

            case 'message_delta':
                cbs.onSSEEvent?.(event);
                break;

            case 'message_stop':
                cbs.onSSEEvent?.(event);
                cbs.onMessageEnd?.({ duration_ms: event.duration_ms });
                break;

            default:
                break;
        }
    }, []);

    const connect = useCallback(() => {
        const ws = getAgentWS();
        if (!ws.isConnected) {
            onError?.(new Error('Kết nối bị gián đoạn, vui lòng thử lại'));
            return;
        }

        setIsConnecting(true);
        isActiveRef.current = true;

        ws.onBinaryMessage = handleBinaryMessage;
        ws.onVoiceEvent = handleVoiceEvent;

        const prevOnEvent = ws.onEvent;
        ws._prevOnEvent = prevOnEvent;
        ws.onEvent = (event) => {
            handleAgentEvent(event);
            prevOnEvent?.(event);
        };

        ws.voiceAgentStart({
            sessionId,
            shareId,
            model,
            language,
            voiceId,
        });
    }, [sessionId, shareId, model, language, voiceId, handleBinaryMessage, handleVoiceEvent, handleAgentEvent, onError]);

    const disconnect = useCallback(() => {
        isActiveRef.current = false;

        const ws = getAgentWS();
        ws.voiceAgentStop();

        ws.onBinaryMessage = null;
        ws.onVoiceEvent = null;
        if (ws._prevOnEvent) {
            ws.onEvent = ws._prevOnEvent;
            ws._prevOnEvent = null;
        }

        cleanupMicrophone();
        cleanupTTSContext();

        setIsConnected(false);
        setIsConnecting(false);
        setIsSpeaking(false);
        setPartialTranscript('');
    }, [cleanupMicrophone, cleanupTTSContext]);

    const startRecording = useCallback(async () => {
        setIsConnecting(true);

        const ws = getAgentWS();
        if (!ws.isConnected) {
            setIsConnecting(false);
            onError?.(new Error('Kết nối bị gián đoạn, vui lòng thử lại'));
            return;
        }

        if (!isActiveRef.current) {
            connect();
            await new Promise(resolve => setTimeout(resolve, 500));
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: SAMPLE_RATE,
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });

            mediaStreamRef.current = stream;
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
                sampleRate: SAMPLE_RATE,
            });

            const actualSampleRate = audioContextRef.current.sampleRate;
            const source = audioContextRef.current.createMediaStreamSource(stream);
            processorRef.current = audioContextRef.current.createScriptProcessor(BUFFER_SIZE, 1, 1);

            let audioChunkCount = 0;

            processorRef.current.onaudioprocess = (e) => {
                if (!isRecordingRef.current) return;

                let inputData = e.inputBuffer.getChannelData(0);
                const { data: normalizedData, gain, originalRMS } = normalizeAudio(inputData);

                let processedData = normalizedData;
                if (actualSampleRate !== SAMPLE_RATE) {
                    processedData = resampleAudio(normalizedData, actualSampleRate, SAMPLE_RATE);
                }

                const pcmData = float32ToPCM16(processedData);
                getAgentWS().sendBinary(pcmData);

                audioChunkCount++;
                if (audioChunkCount === 1) {
                    console.log(`[VoiceChat] First audio chunk: ${pcmData.byteLength} bytes, rate: ${actualSampleRate}Hz → ${SAMPLE_RATE}Hz, gain: ${gain.toFixed(2)}x`);
                }
            };

            source.connect(processorRef.current);
            processorRef.current.connect(audioContextRef.current.destination);

            isRecordingRef.current = true;
            setIsConnecting(false);
            setIsRecording(true);
        } catch (error) {
            console.error('[VoiceChat] Microphone error:', error);
            setIsConnecting(false);
            onError?.(error);
        }
    }, [connect, onError]);

    const stopRecording = useCallback(() => {
        getAgentWS().voiceAgentEndTurn();
        cleanupMicrophone();
    }, [cleanupMicrophone]);

    const interrupt = useCallback(() => {
        getAgentWS().voiceAgentInterrupt();
        stopTTSPlayback();
        setIsSpeaking(false);
    }, [stopTTSPlayback]);

    const uploadAudioFile = useCallback(async (file) => {
        if (!isActiveRef.current) {
            connect();
            await new Promise(resolve => setTimeout(resolve, 500));
        }

        try {
            const arrayBuffer = await file.arrayBuffer();
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

            let audioData = audioBuffer.getChannelData(0);
            if (audioBuffer.sampleRate !== SAMPLE_RATE) {
                audioData = resampleAudio(audioData, audioBuffer.sampleRate, SAMPLE_RATE);
            }

            const { data: normalizedData } = normalizeAudio(audioData);
            const totalChunks = Math.ceil(normalizedData.length / BUFFER_SIZE);

            setIsRecording(true);

            for (let i = 0; i < totalChunks; i++) {
                const start = i * BUFFER_SIZE;
                const end = Math.min(start + BUFFER_SIZE, normalizedData.length);
                const chunk = normalizedData.slice(start, end);

                const paddedChunk = new Float32Array(BUFFER_SIZE);
                paddedChunk.set(chunk);
                const pcmData = float32ToPCM16(paddedChunk);

                getAgentWS().sendBinary(pcmData);

                await new Promise(resolve => setTimeout(resolve, 50));
            }

            await new Promise(resolve => setTimeout(resolve, 500));
            getAgentWS().voiceAgentEndTurn();
            setIsRecording(false);
            audioContext.close();
        } catch (error) {
            console.error('[VoiceChat] Upload error:', error);
            onError?.(error);
            setIsRecording(false);
        }
    }, [connect, onError]);

    useEffect(() => {
        return () => {
            if (isActiveRef.current) {
                disconnect();
            }
        };
    }, [disconnect]);

    return {
        isConnected,
        isConnecting,
        isRecording,
        isSpeaking,
        partialTranscript,
        connect,
        disconnect,
        startRecording,
        stopRecording,
        interrupt,
        uploadAudioFile,
    };
}

export default useVoiceChat;
